<script lang="ts">
    import { flip } from "svelte/animate";
    import { fly, blur } from "svelte/transition";
    import {
        Divider,
        Checkbox,
        DropdownShell,
        Dropdown,
        Button,
    } from "attractions";
    import Fa from "svelte-fa";
    import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
    import type { ConstraintDescriptor } from "../types";
    import ConstraintLabel from "./ConstraintLabel.svelte";
    import { ANIMATION_DURATION, send, receive } from "../utils/animation";

    export let constraints: ConstraintDescriptor[];
    export let feats: string[][];
    export let featCount: number;

    let featFilter = new Set<string>();
    let typeFilter = "All";
    let statusFilter = "All";

    $: featsFlat = feats.flat();
    $: featFilterTitle = getFeatFilterTitle(featFilter, feats);
    $: satisfiedCount = constraints.filter((c) => c.satisfied).length;
    $: violatedCount = constraints.length - satisfiedCount;
    $: visibleConstraints = filterConstraints(
        constraints,
        featFilter,
        typeFilter,
        statusFilter
    );
    $: allVisibleConstraintsSelected = visibleConstraints.every(
        (c) => c.selected
    );

    const getFeatFilterTitle = (featFilter: Set<string>, feats: string[][]) => {
        const total = feats.length * feats[0].length;
        const selected = featFilter.size;
        if (selected % total === 0) {
            return "All";
        }
        return `${selected} Selected`;
    };

    const filterConstraints = (
        constraints: ConstraintDescriptor[],
        featFilter: Set<string>,
        typeFilter: string,
        statusFilter: string
    ) => {
        return constraints.filter((c) => {
            const featCheck =
                featFilter.size === 0 ||
                featFilter.has(c.f1) ||
                featFilter.has(c.f2);

            const typeCheck =
                (typeFilter === "Positive" && c.positive) ||
                (typeFilter === "Negative" && !c.positive) ||
                typeFilter === "All";

            const statusCheck =
                (statusFilter === "Satisfied" && c.satisfied) ||
                (statusFilter === "Violated" && !c.satisfied) ||
                statusFilter === "All";

            return featCheck && typeCheck && statusCheck;
        });
    };

    const handleFeatFilterChange = (e: any, feat: string) => {
        if (e.detail.checked) {
            featFilter.add(feat);
        } else {
            featFilter.delete(feat);
        }
        featFilter = featFilter;
    };

    const handleSelectTypeFilter = (
        selectedFilter: string,
        toggle: () => void
    ) => {
        toggle();
        typeFilter = selectedFilter;
    };

    const handleSelectStatusFilter = (
        selectedFilter: string,
        toggle: () => void
    ) => {
        toggle();
        statusFilter = selectedFilter;
    };

    const toggleConstraint = (e: any, constraint: ConstraintDescriptor) => {
        constraint.selected = e.detail.checked;
        constraints = constraints;
    };

    const toggleVisibleConstraints = (e: any) => {
        visibleConstraints.forEach((c) => (c.selected = e.detail.checked));
        visibleConstraints = visibleConstraints;
    };
</script>

<div class="root-container">
    <span class="font-size-20 margin-bottom-md">Constraints</span>

    <div class="table-header">
        <Checkbox
            class="select-column"
            value="show_all_constraints"
            checked={allVisibleConstraintsSelected}
            on:change={toggleVisibleConstraints}
        />

        <DropdownShell let:toggle>
            <Button on:click={toggle} class="mini-btn">
                {featFilterTitle}
                <div class="margin-left-sm">
                    <Fa icon={faChevronDown} />
                </div>
            </Button>
            <Dropdown>
                <div class="feat-dropdown-container">
                    {#each featsFlat as feat}
                        <div class="feat-container">
                            <Checkbox
                                value={`filter_on_${feat}`}
                                on:change={(e) =>
                                    handleFeatFilterChange(e, feat)}
                            >
                                <div class="filter-item margin-left-sm">
                                    {feat}
                                </div>
                            </Checkbox>
                        </div>
                    {/each}
                </div>
            </Dropdown>
        </DropdownShell>

        <DropdownShell let:toggle>
            <Button on:click={toggle} class="mini-btn">
                {typeFilter}
                <div class="margin-left-sm">
                    <Fa icon={faChevronDown} />
                </div>
            </Button>
            <Dropdown>
                <Button on:click={() => handleSelectTypeFilter("All", toggle)}>
                    <div class="filter-item">All</div>
                </Button>
                <Button
                    on:click={() => handleSelectTypeFilter("Positive", toggle)}
                >
                    <div class="filter-item">Positive</div>
                </Button>
                <Button
                    on:click={() => handleSelectTypeFilter("Negative", toggle)}
                >
                    <div class="filter-item">Negative</div>
                </Button>
            </Dropdown>
        </DropdownShell>

        <DropdownShell let:toggle>
            <Button on:click={toggle} class="mini-btn">
                {statusFilter}
                <div class="margin-left-sm">
                    <Fa icon={faChevronDown} />
                </div>
            </Button>
            <Dropdown>
                <Button
                    on:click={() => handleSelectStatusFilter("All", toggle)}
                >
                    <div class="filter-item">All</div>
                </Button>
                <Button
                    on:click={() =>
                        handleSelectStatusFilter("Satisfied", toggle)}
                >
                    <div class="filter-item">Satisfied</div>
                </Button>
                <Button
                    on:click={() =>
                        handleSelectStatusFilter("Violated", toggle)}
                >
                    <div class="filter-item">Violated</div>
                </Button>
            </Dropdown>
        </DropdownShell>
    </div>

    <Divider class="table-divider" />

    <div class="table-content" style="height: {featCount * 64 + 32}px">
        {#each visibleConstraints as constraint (constraint.key)}
            <div
                class="table-row"
                in:receive={{ key: constraint.key }}
                out:send={{ key: constraint.key }}
                animate:flip={{ duration: ANIMATION_DURATION }}
            >
                <Checkbox
                    class="select-column"
                    value={`show_constraint_${constraint.key}`}
                    checked={constraint.selected}
                    on:change={(e) => toggleConstraint(e, constraint)}
                />
                <ConstraintLabel {constraint} />
                {#key constraint.satisfied}
                    <div
                        class={"constraint-status " +
                            (constraint.satisfied
                                ? "color-green"
                                : "color-red")}
                        in:blur
                    >
                        {constraint.satisfied ? "satisfied" : "violated"}
                    </div>
                {/key}
            </div>
        {/each}
    </div>

    <Divider />
    <div class="status-summary">
        <div>
            <span class="color-red">Violated: </span>
            {#key violatedCount}
                <span in:fly>
                    {violatedCount}
                </span>
            {/key}
        </div>
        <div>
            <span class="color-green font-bold">Satisfied: </span>
            {#key satisfiedCount}
                <span in:fly>
                    {satisfiedCount}
                </span>
            {/key}
        </div>
        <span>Total: {constraints.length}</span>
    </div>
</div>

<style lang="scss">
    @use "common.scss";
    @import "theme.scss";

    .feat-dropdown-container {
        max-height: 24rem;
        overflow-y: auto;
    }

    .feat-container {
        margin: 1rem;
    }

    .root-container {
        padding: 1.618rem;
        border-radius: 1.618rem;
        background: lighten($background, 5%);
        display: flex;
        flex-direction: column;
        align-items: center;
        min-width: 280px;
    }

    .table-content {
        padding-left: 0.75rem;
        padding-right: 0.75rem;
        width: 100%;
        overflow-y: auto;
    }

    .table-row {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 0.25rem;
        margin-bottom: 0.25rem;
    }

    .constraint-status {
        min-width: 48px;
    }

    .feat-column {
        display: flex;
        align-items: center;
    }

    .table-header {
        width: 100%;
        display: flex;
        justify-content: space-between;
    }

    .filter-item {
        width: 4rem;
    }

    .root-container :global(.table-divider) {
        margin-top: 0;
    }

    :global(.mini-btn) {
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
        padding-top: 0.25rem !important;
        padding-bottom: 0.25rem !important;
        margin-bottom: 0.25rem;
    }

    .status-summary {
        width: 100%;
        display: flex;
        justify-content: space-between;
    }
</style>
